import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';
import { AddRoomDialog_hotel$key } from '__generated__/AddRoomDialog_hotel.graphql';
import {
  AddRoomDialogMutation,
  AddRoomDialogMutation$data,
} from '__generated__/AddRoomDialogMutation.graphql';
import { useNavigate } from 'react-router';

type FormFields = {
  name: string;
};

type Props = {
  hotel: AddRoomDialog_hotel$key;
};

const initial = {
  name: '',
};

const AddRoomDialog: FC<Props> = props => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler<AddRoomDialogMutation$data>();
  const navigate = useNavigate();

  const hotel = useFragment(
    graphql`
      fragment AddRoomDialog_hotel on Hotel {
        id

        rooms(first: $first, after: $after, search: $search, sort: $sort) {
          __id
        }
      }
    `,
    props.hotel
  );

  const [add, adding] = useMutation<AddRoomDialogMutation>(graphql`
    mutation AddRoomDialogMutation(
      $id: ID!
      $input: AddRoomToHotelInput!
      $connections: [ID!]!
    ) {
      addRoomToHotel(id: $id, input: $input) {
        node @prependNode(connections: $connections, edgeTypeName: "RoomEdge") {
          id

          ...HotelEditRoomsRow_room
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (values: FormFields) => {
      const connectionId = hotel.rooms?.__id;

      if (!connectionId) return;

      add({
        variables: {
          id: hotel.id,
          connections: [connectionId],
          input: {
            name: values.name,
          },
        },

        ...errorHandler({
          onCompleted: data => {
            navigate(`/rooms/${data.addRoomToHotel.node.id}`);
          },
        }),
      });
    },
    [add, errorHandler, hotel.id, hotel.rooms?.__id, navigate]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas kambarys</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Čia sukurtus viešbučio kambarius, vėliau galima pridėti prie
            kelionės pasiūlymo, kelionės užsakymui
          </DialogContentText>

          <TextField
            autoFocus
            name="name"
            label="Pavadinimas"
            fullWidth
            variant="standard"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Saugoti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddRoomDialog;
